import type {
    FC
} from "react";
import {
    type GestureResponderEvent,
    type NativeSyntheticEvent,
    type TargetedEvent,
    Pressable,
    Platform,
    View
} from "react-native";
import type {
    SidebarMenuButtonRowProps
} from "./type";
import type ISidebarMenuButtonProps from "./type";
import stylesheet, {
    SIDEBAR_MENU_BUTTON_NAME_VARIANT,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    INTERACTION_STATE_DATA_KEY,
    useInteractionState
} from "../../../../helpers/interaction";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    useSidebarRootContext,
    useSidebarItemContext,
    useSidebarContext
} from "../../context";
import type {
    SidebarWebPropsType
} from "../../type";
import {
    ChevronsUpDown as ChevronsUpDownIcon
} from "lucide-react-native";
import {
    getSidebarActionPlacement,
    SIDEBAR_SUB_LABEL_VARIANT,
    getSidebarActiveWeight,
    getSidebarRowMeasures,
    getSidebarRowRadius,
    getSidebarRowColors,
    getSidebarMeasures
} from "../../stylesheet";
import {
    getMirrorStyle,
    isMirroredIcon
} from "../../../../helpers/direction";
import {
    splitControlStyle
} from "../../../../helpers/theme/control";
import {
    concentric
} from "../../../../helpers/theme/concentric";
import {
    getSidebarOverlaySide
} from "../../util";
import DisclosureChevron from "../../../collapsible/internal/disclosureChevron";
import StateLayer from "../../../stateLayer";
import Tooltip from "../../../tooltip";
import Text from "../../../text";

const SidebarMenuButtonRow: FC<SidebarMenuButtonRowProps> = ({
    customTheme: customThemeProp,
    endIcon: EndIconComponent,
    onFocus: onFocusProp,
    icon: IconComponent,
    isDisabled = false,
    onBlur: onBlurProp,
    variant = "plain",
    isActive = false,
    size = "medium",
    onHoverOut,
    onPressOut,
    renderIcon,
    onHoverIn,
    onPressIn,
    subTitle,
    onPress,
    style,
    title,
    href,
    ...props
}) => {
    const provider = useSidebarContext({
        part: "Sidebar.MenuButton"
    });

    const root = useSidebarRootContext({
        part: "Sidebar.MenuButton"
    });

    const item = useSidebarItemContext();

    const customTheme = customThemeProp ?? root?.customTheme;

    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        avatarSizes,
        typography,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const pointerType = usePointerType();

    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    const isCollapsed = root?.isCollapsed ?? false;

    const hasSubToggle = !!item?.hasSub && !!item.isCollapsible && !isCollapsed;

    const isRowActive = isActive || (isCollapsed && !!item?.hasActiveSub);

    const isPopup = props["aria-haspopup"] !== undefined && props["aria-haspopup"] !== false;

    const isPopupOpen = isPopup && props["aria-expanded"] === true;

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        onFocus: (event: NativeSyntheticEvent<TargetedEvent>) => {
            item?.setIsFocused(true);

            onFocusProp?.(event);
        },
        onBlur: (event: NativeSyntheticEvent<TargetedEvent>) => {
            item?.setIsFocused(false);

            onBlurProp?.(event);
        },
        onHoverOut,
        onPressOut,
        onHoverIn,
        onPressIn
    });

    const measures = getSidebarMeasures({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        spaces
    });

    const rowMeasures = getSidebarRowMeasures({
        square: measures.square,
        controlHeights,
        touchTargets,
        pointerType,
        iconSizes,
        size
    });

    const actionPlacement = item?.hasAction ? getSidebarActionPlacement({
        height: rowMeasures.height,
        inset: rowMeasures.inset,
        touchTargets,
        pointerType,
        iconSizes,
        spaces
    }) : undefined;

    const currentColors = getSidebarRowColors({
        isActive: isRowActive,
        isDisabled,
        colors
    });

    const currentRadius = getSidebarRowRadius({
        variant: root?.variant ?? "sidebar",
        measures,
        radiuses,
        borders
    });

    const avatarSize = avatarSizes.xSmall;

    const logoRadius = concentric({
        inset: (measures.square - avatarSize) / 2,
        outer: currentRadius,
        role: radiuses.form
    });

    const {
        container: containerDynamicStyle,
        surface: surfaceDynamicStyle,
        icon: iconDynamicStyle
    } = useStyles({
        actionPlacement,
        controlHeights,
        isFocusVisible,
        currentColors,
        currentRadius,
        shapeConfigs,
        isCollapsed,
        rowMeasures,
        avatarSize,
        isDisabled,
        measures,
        borders,
        variant,
        colors,
        spaces
    });

    const {
        containerStyle,
        surfaceStyle
    } = splitControlStyle({
        style
    });

    const handlePress = (event: GestureResponderEvent) => {
        if(isDisabled) {
            return;
        }

        onPress?.(event);

        if(hasSubToggle && item) {
            item.handleExpandedChange(!item.isExpanded);

            return;
        }

        if(root?.isPhone && !isPopup) {
            provider?.handleOpenChange(false);
        }
    };

    const isLink = Platform.OS === "web" && !!href && !hasSubToggle && !isDisabled;

    const webProps: SidebarWebPropsType & {
        "aria-current"?: "page";
        href?: string;
    } = Platform.OS === "web" ? {
        "aria-current": isActive ? "page" : undefined,
        dataSet: isDisabled ? {
            [INTERACTION_STATE_DATA_KEY]: "disabled"
        } : interactionHandlers.dataSet,
        href: isLink ? href : undefined
    } : {};

    const nativeSelectionProps = Platform.OS === "web" ? {} : {
        "aria-selected": props["aria-selected"] ?? (isActive ? true : undefined)
    };

    const getCollapsedLabel = () => {
        if(!item?.badgeText) {
            return title;
        }

        return `${title}, ${item.badgeText}${item.badgeLabel ? ` ${item.badgeLabel}` : ""}`;
    };

    const renderStart = () => {
        if(renderIcon) {
            return <View
                style={iconDynamicStyle}
            >
                {
                    renderIcon({
                        borderRadius: logoRadius,
                        size: avatarSize
                    })
                }
            </View>;
        }

        if(!IconComponent) {
            return null;
        }

        return <IconComponent
            customColor={currentColors.iconColor}
            color={currentColors.iconColorKey}
            size={rowMeasures.iconSize}
        />;
    };

    const renderTitle = () => {
        if(isCollapsed) {
            return null;
        }

        const titleStyle = isRowActive ? {
            fontWeight: getSidebarActiveWeight({
                typography
            })
        } : undefined;

        if(subTitle) {
            return <View
                style={stylesheet.textBlock}
            >
                <Text
                    variant={SIDEBAR_MENU_BUTTON_NAME_VARIANT}
                    customColor={currentColors.titleColor}
                    customTheme={customTheme}
                    style={[
                        stylesheet.title,
                        titleStyle
                    ]}
                    numberOfLines={1}
                >
                    {title}
                </Text>
                <Text
                    customColor={currentColors.subTitleColor}
                    variant={SIDEBAR_SUB_LABEL_VARIANT}
                    customTheme={customTheme}
                    style={stylesheet.title}
                    numberOfLines={1}
                >
                    {subTitle}
                </Text>
            </View>;
        }

        return <View
            style={stylesheet.textBlock}
        >
            <Text
                customColor={currentColors.titleColor}
                variant={rowMeasures.labelVariant}
                customTheme={customTheme}
                style={[
                    stylesheet.title,
                    titleStyle
                ]}
                numberOfLines={1}
            >
                {title}
            </Text>
        </View>;
    };

    const renderEnd = () => {
        if(isCollapsed) {
            return null;
        }

        if(hasSubToggle) {
            return <DisclosureChevron
                color={currentColors.iconColor}
                isOpen={!!item?.isExpanded}
                customTheme={customTheme}
                style={stylesheet.end}
                size={iconSizes.small}
            />;
        }

        if(isPopup) {
            return <View
                style={stylesheet.end}
            >
                <ChevronsUpDownIcon
                    color={currentColors.iconColor}
                    size={iconSizes.small}
                />
            </View>;
        }

        if(!EndIconComponent) {
            return null;
        }

        return <View
            style={[
                stylesheet.end,
                getMirrorStyle({
                    isMirrored: isMirroredIcon({
                        name: EndIconComponent.displayName
                    }),
                    direction
                })
            ]}
        >
            <EndIconComponent
                customColor={currentColors.iconColor}
                color={currentColors.iconColorKey}
                size={iconSizes.small}
            />
        </View>;
    };

    return <Pressable
        {...props}
        {...interactionHandlers}
        {...webProps}
        {...nativeSelectionProps}
        aria-label={isCollapsed ? props["aria-label"] ?? props.accessibilityLabel ?? getCollapsedLabel() : props["aria-label"]}
        aria-expanded={hasSubToggle ? item?.isExpanded : props["aria-expanded"]}
        aria-controls={hasSubToggle ? item?.subId : props["aria-controls"]}
        role={isLink ? "link" : "button"}
        aria-disabled={isDisabled}
        style={[
            containerStyle,
            stylesheet.container,
            containerDynamicStyle
        ]}
        disabled={isDisabled}
        onPress={handlePress}
    >
        <View
            style={[
                surfaceStyle,
                stylesheet.surface,
                surfaceDynamicStyle
            ]}
        >
            <StateLayer
                isPressed={!isDisabled && (isPressed || isPopupOpen)}
                isHovered={!isDisabled && isHovered}
                borderRadius={currentRadius}
                customTheme={customTheme}
                overlayColor="default"
                borderWidth={0}
            />
            {renderStart()}
            {renderTitle()}
            {item?.badge}
            {renderEnd()}
        </View>
    </Pressable>;
};

const SidebarMenuButton: FC<ISidebarMenuButtonProps> = ({
    tooltip,
    ...props
}) => {
    const root = useSidebarRootContext({
        part: "Sidebar.MenuButton"
    });

    const item = useSidebarItemContext();

    const pointerType = usePointerType();

    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    const isTooltipSkipped = tooltip === false || (!!props.isDisabled && pointerType === "coarse");

    if(!root?.isCollapsed || (item?.hasSub && tooltip === undefined) || isTooltipSkipped) {
        return <SidebarMenuButtonRow
            {...props}
        />;
    }

    const tooltipProps = typeof tooltip === "object" ? tooltip : {
        title: typeof tooltip === "string" ? tooltip : item?.badgeText ? `${props.title} · ${item.badgeText}` : props.title
    };

    return <Tooltip
        isOpen={props["aria-haspopup"] && props["aria-expanded"] ? false : undefined}
        customTheme={props.customTheme ?? root.customTheme}
        side={getSidebarOverlaySide({
            side: root.side,
            direction
        })}
        {...tooltipProps}
    >
        <SidebarMenuButtonRow
            {...props}
        />
    </Tooltip>;
};
export default SidebarMenuButton;
